import type { CombinedFieldType, DynamicFormFieldGroupWithOptionalId } from '../@types/forms';

/**
 * Adds standardized Yes/No/N/A answers to TEXTUAL_QUESTIONNAIRE fields with YES_NO_NA preset type.
 * This utility function is essential for generated forms where preset answer options need to be populated.
 *
 * The function performs deep cloning to avoid mutating the original data structure and processes
 * TEXTUAL_QUESTIONNAIRE fields to add preset answers when the tqPresetAnswerType is 'YES_NO_NA'.
 *
 * For TEXTUAL_QUESTIONNAIRE fields with 'YES_NO_NA' preset answer type, the function will:
 * - Add standardized Yes/No/N/A answer options
 * - Each option includes proper translations and sequential positioning
 * - All answers are assigned unique IDs using crypto.randomUUID()
 *
 * @param fieldGroups - Array of form field groups that may contain TEXTUAL_QUESTIONNAIRE fields
 * @returns A new array of field groups with YES_NO_NA preset answers populated
 *
 * @example
 * ```typescript
 * const formWithPresetAnswers = addIdToGeneratedForm(generatedFormData);
 * // TEXTUAL_QUESTIONNAIRE fields with YES_NO_NA preset now have Yes/No/N/A options
 * ```
 */
export const addIdToGeneratedForm = (
  fieldGroups: Array<DynamicFormFieldGroupWithOptionalId>
): Array<DynamicFormFieldGroupWithOptionalId> => {
  let clonedFieldGroups = structuredClone(fieldGroups);

  clonedFieldGroups = clonedFieldGroups.map((group) => {
    group.fields = group.fields.map((field) => {
      if ((field.fieldType as CombinedFieldType) === 'TEXTUAL_QUESTIONNAIRE') {
        if (field.tqPresetAnswerType === 'YES_NO_NA') {
          field.answers?.push({
            caption: {
              translations: [
                {
                  localeId: 1,
                  value: 'Yes',
                },
              ],
            },
            position: 0,
            id: crypto.randomUUID(),
          });
          field.answers?.push({
            caption: {
              translations: [
                {
                  localeId: 1,
                  value: 'No',
                },
              ],
            },
            position: 1,
            id: crypto.randomUUID(),
          });
          field.answers?.push({
            caption: {
              translations: [
                {
                  localeId: 1,
                  value: 'N/A',
                },
              ],
            },
            position: 2,
            id: crypto.randomUUID(),
          });
        }
      }
      return field;
    });
    return group;
  });
  return clonedFieldGroups;
};
